import React, { createContext, useContext, useMemo } from 'react';
import {motion } from 'framer-motion';
import { Box} from '@chakra-ui/react';
import { normalizeAnimacionesMicrositio } from './micrositeUtils.js';

const MotionBox = motion.create(Box);

const easeOut = [0.22, 1, 0.36, 1];

const RevealCtx = createContext(null);

function computeRevealPhysics(norm) {
  const k = norm.intensidad / 100;
  const t = norm.rapidez / 100;

  /** Desplazamiento (px): casi imperceptible en 0, muy marcado en 100. Calibrado para que ~50 ≈ ~29px de antes. */
  const ampStrong = 1 + k * 56;
  const ampSoft = Math.max(1, ampStrong * (0.2 + 0.45 * (1 - k * 0.85)));

  /** Duración base (s) según intensidad: más “snappy” al subir la intensidad. En ~50 ≈ 0,44s como antes. */
  let durStrong = Math.max(0.09, 0.78 - k * 0.68);
  let durSoft = Math.max(0.08, durStrong * (0.82 - k * 0.12));

  /**
   * Rapidez 0..100 → factor sobre esa duración (50 = 1×).
   * pow(22, 0.5 - t): t=0 ≈ 4,7× más lento, t=1 ≈ 0,21× (más de 20× de rango extremo a extremo).
   */
  const durMult = Math.pow(22, 0.5 - t);
  durStrong *= durMult;
  durSoft *= durMult;
  durStrong = Math.min(4.8, Math.max(0.055, durStrong));
  durSoft = Math.min(4.2, Math.max(0.05, durSoft));

  /** Cuánto debe entrar el bloque en el viewport: rango amplio (antes ~0,06–0,22). */
  const amountStrong = 0.02 + k * 0.28;
  const amountSoft = Math.max(0.015, amountStrong * (0.5 + 0.38 * (1 - k)));

  const block = (amp) => {
    if (norm.modo === 'horizontal') {
      return { initial: { opacity: 0, x: -amp }, whileInView: { opacity: 1, x: 0 } };
    }
    if (norm.modo === 'opacidad') {
      return {
        initial: { opacity: 0 },
        whileInView: { opacity: 1 },
      };
    }
    return { initial: { opacity: 0, y: amp }, whileInView: { opacity: 1, y: 0 } };
  };

  return {
    norm,
    strong: block(ampStrong),
    soft: block(ampSoft),
    durStrong,
    durSoft,
    amountStrong,
    amountSoft,
  };
}

const FALLBACK_PHYSICS = computeRevealPhysics(normalizeAnimacionesMicrositio());

export function MicrositeRevealProvider({ micrositeConfig, children }) {
  const value = useMemo(() => {
    const norm = normalizeAnimacionesMicrositio(micrositeConfig?.animacionesMicrositio);
    return computeRevealPhysics(norm);
  }, [micrositeConfig]);
  return <RevealCtx.Provider value={value}>{children}</RevealCtx.Provider>;
}

function useRevealPhysics() {
  const ctx = useContext(RevealCtx);
  return ctx || FALLBACK_PHYSICS;
}

/**
 * Aparición al entrar en el viewport al hacer scroll; se repite al salir y volver a pasar (viewport.once = false).
 * Respeta `micrositeConfig.animacionesMicrositio` vía `MicrositeRevealProvider`.
 */
export function ScrollReveal({
  children,
  delay = 0,
  variant = 'strong',
  ...boxProps
}) {
  const physics = useRevealPhysics();
  if (!physics.norm.enabled) {
    return <Box {...boxProps}>{children}</Box>;
  }
  const motionProps = variant === 'soft' ? physics.soft : physics.strong;
  const duration = variant === 'soft' ? physics.durSoft : physics.durStrong;
  const amount = variant === 'soft' ? physics.amountSoft : physics.amountStrong;

  return (
    <MotionBox
      initial={motionProps.initial}
      whileInView={motionProps.whileInView}
      viewport={{ once: false, amount }}
      transition={{ duration, delay, ease: easeOut }}
      {...boxProps}
    >
      {children}
    </MotionBox>
  );
}

/** Variante más sutil para bloques secundarios (menos desplazamiento / viewport más laxo vía amount). */
export function ScrollRevealSoft({ children, delay = 0, ...boxProps }) {
  return (
    <ScrollReveal delay={delay} variant="soft" {...boxProps}>
      {children}
    </ScrollReveal>
  );
}
